"use client";

import { useEffect, useRef } from "react";
import { STALE_RESUME_REFRESH_MS, shouldRefreshOnResume } from "../pwa";

export interface UseStaleResumeRefreshOptions {
  onRefresh: () => void | Promise<void>;
  staleAfterMs?: number;
  enabled?: boolean;
  now?: () => number;
}

function currentTimeMs(): number {
  return Date.now();
}

export function useStaleResumeRefresh({
  onRefresh,
  staleAfterMs = STALE_RESUME_REFRESH_MS,
  enabled = true,
  now = currentTimeMs,
}: UseStaleResumeRefreshOptions): void {
  const onRefreshRef = useRef(onRefresh);
  const hiddenAtMsRef = useRef<number | null>(null);
  const refreshingRef = useRef(false);
  const nowRef = useRef(now);

  onRefreshRef.current = onRefresh;
  nowRef.current = now;

  useEffect(() => {
    if (!enabled) {
      hiddenAtMsRef.current = null;
      return;
    }

    const markHidden = () => {
      hiddenAtMsRef.current = nowRef.current();
    };

    const maybeRefresh = () => {
      const hiddenAt = hiddenAtMsRef.current;
      hiddenAtMsRef.current = null;
      if (refreshingRef.current) {
        return;
      }
      if (!shouldRefreshOnResume(hiddenAt, nowRef.current(), staleAfterMs)) {
        return;
      }

      refreshingRef.current = true;
      try {
        const result = onRefreshRef.current();
        if (result && typeof result.then === "function") {
          void result.catch(() => undefined).finally(() => {
            refreshingRef.current = false;
          });
          return;
        }
      } catch {
        // Ignore refresh errors; the next stale resume can try again.
      }
      refreshingRef.current = false;
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        markHidden();
        return;
      }
      maybeRefresh();
    };

    const onWindowFocus = (event: FocusEvent) => {
      if (event.target !== window) {
        return;
      }
      maybeRefresh();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", markHidden);
    window.addEventListener("pageshow", maybeRefresh);
    window.addEventListener("freeze", markHidden);
    window.addEventListener("resume", maybeRefresh);
    window.addEventListener("focus", onWindowFocus);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", markHidden);
      window.removeEventListener("pageshow", maybeRefresh);
      window.removeEventListener("freeze", markHidden);
      window.removeEventListener("resume", maybeRefresh);
      window.removeEventListener("focus", onWindowFocus);
    };
  }, [enabled, staleAfterMs]);
}
